import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  billingProblems,
  computeTotalCents,
  isExpired,
  taxLinesFor,
  type AdminProposal,
  type BillingCycle,
  type CompanyProfile,

  type BillingDetails,
  type ProposalItem,
  type PublicProposal,
} from "./proposals-types";


type Ctx = {
  supabase: import("@supabase/supabase-js").SupabaseClient;
  userId: string;
};

async function assertAdmin(ctx: Ctx) {
  const { data, error } = await ctx.supabase.rpc("has_role", {
    _user_id: ctx.userId,
    _role: "admin",
  });
  if (error) throw new Error("Role check failed: " + error.message);
  if (!data) throw new Error("Forbidden");
}

function requestMeta() {
  const request = getRequest();
  const headers = request?.headers;
  const ip =
    headers?.get("cf-connecting-ip") ??
    headers?.get("x-forwarded-for")?.split(",")[0]?.trim() ??
    null;
  const ua = headers?.get("user-agent") ?? null;
  const origin = request ? new URL(request.url).origin : "https://vibing.inc";
  return { ip, ua, origin };
}

/* ------------------------- Admin ------------------------- */

export const listProposals = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<AdminProposal[]> => {
    await assertAdmin(context as Ctx);
    const { loadAdminProposals } = await import("./proposals.server");
    return loadAdminProposals();
  });

export const getCompanyProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<CompanyProfile> => {
    await assertAdmin(context as Ctx);
    const { loadCompanyProfile } = await import("./proposals.server");
    return loadCompanyProfile();
  });

export const updateCompanyProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { patch: Partial<CompanyProfile> }) => {
    const patch = input?.patch ?? {};
    const text = (v: unknown) => (typeof v === "string" ? v.trim() : "");
    const required: Array<keyof CompanyProfile> = [
      "legal_name",
      "owner_name",
      "street",
      "postcode",
      "city",
      "country",
      "email",
    ];
    for (const key of required) {
      if (key in patch && text(patch[key]).length < 2) {
        throw new Error(`"${String(key)}" is required.`);
      }
    }
    return { patch };
  })
  .handler(async ({ context, data }): Promise<CompanyProfile> => {
    await assertAdmin(context as Ctx);
    const { saveCompanyProfile } = await import("./proposals.server");
    return saveCompanyProfile(data.patch);
  });


export const getStudioCapacity = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context as Ctx);
    const { getCapacity, summarizeCapacity } = await import("./calendar.server");
    return summarizeCapacity(await getCapacity());
  });

export const draftProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { brief: string; proposalId?: string }) => {
    const brief = (input.brief ?? "").trim();
    if (brief.length < 10) throw new Error("Give the assistant a bit more context (10+ characters).");
    if (brief.length > 6000) throw new Error("Brief is too long.");
    return { brief, proposalId: input.proposalId };
  })
  .handler(async ({ data, context }): Promise<{ ok: true; id: string } | { error: string }> => {
    const ctx = context as Ctx;
    await assertAdmin(ctx);
    const {
      generateProposalDraft,
      newToken,
      nextProposalNumber,
      replaceProposalItems,
      recomputeProposalTotal,
      logProposalEvent,
    } = await import("./proposals.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const db = supabaseAdmin as any;

    const draft = await generateProposalDraft(data.brief);
    if ("error" in draft) return { error: draft.error };

    const patch = {
      title: draft.title,
      intro: draft.intro,
      scope: draft.scope,
      timeline: draft.timeline,
      timeline_points: draft.timeline_points,
      timeline_note: draft.timeline_note,
      earliest_start_date: draft.earliest_start_date,
      duration_weeks_min: draft.duration_weeks_min,
      duration_weeks_max: draft.duration_weeks_max,
      capacity_note: draft.capacity_note,
      contract_body: draft.contract_body,
      deliverables: draft.deliverables,
      assumptions: draft.assumptions,
      currency: draft.currency,
      language: draft.language,
      billing_cycle: draft.billing_cycle,
    };

    let id = data.proposalId ?? null;
    if (id) {
      const { data: existing } = await db.from("proposals").select("status").eq("id", id).maybeSingle();
      if (!existing) return { error: "Proposal not found" };
      if (existing.status === "signed" || existing.status === "paid") {
        return { error: "This proposal is signed and locked." };
      }
      await db.from("proposals").update(patch).eq("id", id);
    } else {
      const { data: created, error } = await db
        .from("proposals")
        .insert({
          ...patch,
          token: newToken(),
          number: await nextProposalNumber(),
          created_by: ctx.userId,
          valid_until: new Date(Date.now() + 14 * 86400_000).toISOString().slice(0, 10),
        })
        .select("id")
        .single();
      if (error || !created) return { error: error?.message ?? "Could not create proposal" };
      id = created.id as string;
    }

    await replaceProposalItems(id!, draft.items);
    await recomputeProposalTotal(id!);
    await logProposalEvent(id!, "drafted", { meta: { brief: data.brief.slice(0, 500) } });
    return { ok: true, id: id! };
  });

export const createBlankProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ ok: true; id: string } | { error: string }> => {
    const ctx = context as Ctx;
    await assertAdmin(ctx);
    const { newToken, nextProposalNumber, logProposalEvent } = await import("./proposals.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabaseAdmin as any)
      .from("proposals")
      .insert({
        token: newToken(),
        number: await nextProposalNumber(),
        title: "Untitled proposal",
        created_by: ctx.userId,
        valid_until: new Date(Date.now() + 14 * 86400_000).toISOString().slice(0, 10),
      })
      .select("id")
      .single();
    if (error || !data) return { error: error?.message ?? "Could not create proposal" };
    await logProposalEvent(data.id as string, "created");
    return { ok: true, id: data.id as string };
  });

export const saveProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: {
      id: string;
      fields: {
        title?: string;
        client_name?: string | null;
        client_email?: string | null;
        company?: string | null;
        intro?: string | null;
        scope?: string | null;
        timeline?: string | null;
        timeline_points?: unknown;
        timeline_note?: string | null;
        earliest_start_date?: string | null;
        duration_weeks_min?: number | null;
        duration_weeks_max?: number | null;
        capacity_note?: string | null;
        contract_body?: string | null;
        deliverables?: string | null;
        assumptions?: string | null;
        currency?: string;
        language?: "en" | "de";
        billing_cycle?: BillingCycle;
        valid_until?: string | null;
      };
      items?: Array<{
        title: string;
        description?: string | null;
        quantity?: number;
        unit_amount_cents: number;
        is_addon?: boolean;
        default_selected?: boolean;
        category?: string;
      }>;
    }) => {
      if (!input.id) throw new Error("Missing proposal id");
      return input;
    },
  )
  .handler(async ({ data, context }): Promise<{ ok: true; total: number } | { error: string }> => {
    await assertAdmin(context as Ctx);
    const { replaceProposalItems, recomputeProposalTotal } = await import("./proposals.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const db = supabaseAdmin as any;

    const { data: existing } = await db.from("proposals").select("status").eq("id", data.id).maybeSingle();
    if (!existing) return { error: "Proposal not found" };
    if (existing.status === "signed" || existing.status === "paid") {
      return { error: "This proposal is signed and locked — duplicate it to make changes." };
    }

    const fields = { ...data.fields };
    if (fields.currency) fields.currency = fields.currency.toLowerCase();
    if (fields.timeline_points !== undefined) {
      const { normalizeTimeline } = await import("./proposals-types");
      fields.timeline_points = normalizeTimeline(fields.timeline_points);
    }
    await db.from("proposals").update(fields).eq("id", data.id);
    if (data.items) await replaceProposalItems(data.id, data.items);
    const total = await recomputeProposalTotal(data.id);
    return { ok: true, total };
  });

export const deleteProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string }) => {
    if (!input.id) throw new Error("Missing id");
    return input;
  })
  .handler(async ({ data, context }): Promise<{ ok: true } | { error: string }> => {
    await assertAdmin(context as Ctx);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabaseAdmin as any).from("proposals").delete().eq("id", data.id);
    if (error) return { error: error.message };
    return { ok: true };
  });

export const setProposalStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string; status: "draft" | "void" }) => {
    if (!input.id) throw new Error("Missing id");
    if (input.status !== "draft" && input.status !== "void") throw new Error("Invalid status");
    return input;
  })
  .handler(async ({ data, context }): Promise<{ ok: true } | { error: string }> => {
    await assertAdmin(context as Ctx);
    const { logProposalEvent } = await import("./proposals.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabaseAdmin as any)
      .from("proposals")
      .update({ status: data.status })
      .eq("id", data.id);
    if (error) return { error: error.message };
    await logProposalEvent(data.id, data.status === "void" ? "voided" : "reopened");
    return { ok: true };
  });

export const duplicateProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string }) => {
    if (!input.id) throw new Error("Missing id");
    return input;
  })
  .handler(async ({ data, context }): Promise<{ ok: true; id: string } | { error: string }> => {
    const ctx = context as Ctx;
    await assertAdmin(ctx);
    const { newToken, nextProposalNumber, recomputeProposalTotal } = await import("./proposals.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const db = supabaseAdmin as any;

    const { data: src } = await db.from("proposals").select("*").eq("id", data.id).maybeSingle();
    if (!src) return { error: "Proposal not found" };
    const { data: items } = await db
      .from("proposal_items")
      .select("*")
      .eq("proposal_id", data.id)
      .order("position", { ascending: true });

    const { data: created, error } = await db
      .from("proposals")
      .insert({
        token: newToken(),
        number: await nextProposalNumber(),
        title: src.title,
        intro: src.intro,
        scope: src.scope,
        timeline: src.timeline,
        timeline_points: src.timeline_points,
        timeline_note: src.timeline_note,
        earliest_start_date: src.earliest_start_date,
        duration_weeks_min: src.duration_weeks_min,
        duration_weeks_max: src.duration_weeks_max,
        capacity_note: src.capacity_note,
        contract_body: src.contract_body,
        deliverables: src.deliverables,
        assumptions: src.assumptions,
        currency: src.currency,
        language: src.language,
        billing_cycle: src.billing_cycle,
        client_name: src.client_name,
        client_email: src.client_email,
        company: src.company,
        created_by: ctx.userId,
        valid_until: new Date(Date.now() + 14 * 86400_000).toISOString().slice(0, 10),
      })
      .select("id")
      .single();
    if (error || !created) return { error: error?.message ?? "Could not duplicate" };

    const rows = ((items ?? []) as ProposalItem[]).map((i, index) => ({
      proposal_id: created.id,
      position: index,
      title: i.title,
      description: i.description,
      quantity: i.quantity,
      unit_amount_cents: i.unit_amount_cents,
      is_addon: i.is_addon,
      default_selected: i.default_selected,
      category: i.category,
    }));
    if (rows.length) await db.from("proposal_items").insert(rows);
    await recomputeProposalTotal(created.id as string);
    return { ok: true, id: created.id as string };
  });

export const sendProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string }) => {
    if (!input.id) throw new Error("Missing id");
    return input;
  })
  .handler(async ({ data, context }): Promise<{ ok: true; url: string } | { error: string }> => {
    await assertAdmin(context as Ctx);
    const { logProposalEvent, recomputeProposalTotal } = await import("./proposals.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const db = supabaseAdmin as any;

    const { data: p } = await db.from("proposals").select("*").eq("id", data.id).maybeSingle();
    if (!p) return { error: "Proposal not found" };
    if (!p.client_email) return { error: "Add a client email before sending." };
    if (p.status === "signed" || p.status === "paid") return { error: "Already signed." };

    const total = await recomputeProposalTotal(data.id);
    const { origin } = requestMeta();
    const url = `${origin}/p/${p.token}`;

    await db
      .from("proposals")
      .update({ status: "sent", sent_at: new Date().toISOString() })
      .eq("id", data.id);
    await logProposalEvent(data.id, "sent", { meta: { to: p.client_email } });

    try {
      const [{ enqueueBrandedEmail }, { ProposalLinkEmail }, { formatMoney, cycleLabel }] =
        await Promise.all([
          import("./email/transactional.server"),
          import("./email-templates/proposal-link"),
          import("./proposals-types"),
        ]);
      const priceLabel = `${formatMoney(total, p.currency)} ${cycleLabel(p.billing_cycle)}`;
      await enqueueBrandedEmail({
        to: p.client_email as string,
        subject: `${p.number} — ${p.title}`,
        label: "proposal-link",
        locale: (p.language as "en" | "de") ?? "en",
        element: ProposalLinkEmail({
          recipientName: p.client_name,
          proposalNumber: p.number,
          title: p.title,
          priceLabel,
          validUntil: p.valid_until,
          proposalUrl: url,
        }),
      });
    } catch (e) {
      console.error("proposal email failed", e);
    }

    return { ok: true, url };
  });

/** Activate the public link without sending an email (draft -> sent). */
export const publishProposalLink = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string }) => {
    if (!input.id) throw new Error("Missing id");
    return input;
  })
  .handler(async ({ data, context }): Promise<{ ok: true; url: string } | { error: string }> => {
    await assertAdmin(context as Ctx);
    const { logProposalEvent, recomputeProposalTotal } = await import("./proposals.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const db = supabaseAdmin as any;

    const { data: p } = await db
      .from("proposals")
      .select("id, token, status")
      .eq("id", data.id)
      .maybeSingle();
    if (!p) return { error: "Proposal not found" };

    const { origin } = requestMeta();
    const url = `${origin}/p/${p.token}`;
    if (p.status !== "draft") return { ok: true, url };

    await recomputeProposalTotal(data.id);
    await db
      .from("proposals")
      .update({ status: "sent", sent_at: new Date().toISOString() })
      .eq("id", data.id);
    await logProposalEvent(data.id, "sent", { meta: { via: "link" } });

    return { ok: true, url };
  });


/* ------------------------- Public ------------------------- */

export const getPublicProposal = createServerFn({ method: "POST" })
  .inputValidator((input: { token: string }) => {
    if (!input.token || !/^[a-f0-9]{16,96}$/.test(input.token)) throw new Error("Invalid link");
    return input;
  })
  .handler(async ({ data }): Promise<PublicProposal | null> => {
    const {
      loadProposalByToken,
      toPublicProposal,
      logProposalEvent,
      loadCompanyProfile,
      loadInvoiceForProposal,
    } = await import("./proposals.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const db = supabaseAdmin as any;

    const found = await loadProposalByToken(data.token);
    if (!found) return null;
    const { proposal, items } = found;
    if (proposal.status === "draft" || proposal.status === "void") return null;

    const { data: sig } = await db
      .from("proposal_signatures")
      .select("signer_name, signed_at, selected_addon_ids, total_cents, content_hash")
      .eq("proposal_id", proposal.id)
      .maybeSingle();

    if (proposal.status === "sent") {
      const { ip, ua } = requestMeta();
      await db
        .from("proposals")
        .update({ status: "viewed", viewed_at: new Date().toISOString() })
        .eq("id", proposal.id);
      await logProposalEvent(proposal.id, "viewed", { ip, ua });
      proposal.status = "viewed";
      proposal.viewed_at = new Date().toISOString();
    }

    const [company, invoice] = await Promise.all([
      loadCompanyProfile(),
      loadInvoiceForProposal(proposal.id),
    ]);
    return toPublicProposal(proposal, items, sig ?? null, { company, invoice });
  });


// Admin-only preview: renders a proposal at any status (including draft)
// without tracking a view or writing an audit event.
export const getProposalPreview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { token: string }) => {
    if (!input.token || !/^[a-f0-9]{16,96}$/.test(input.token)) throw new Error("Invalid link");
    return input;
  })
  .handler(async ({ data, context }): Promise<PublicProposal | null> => {
    await assertAdmin(context as Ctx);
    const { loadProposalByToken, toPublicProposal, loadCompanyProfile, loadInvoiceForProposal } =
      await import("./proposals.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const db = supabaseAdmin as any;

    const found = await loadProposalByToken(data.token);
    if (!found) return null;
    const { proposal, items } = found;

    const { data: sig } = await db
      .from("proposal_signatures")
      .select("signer_name, signed_at, selected_addon_ids, total_cents, content_hash")
      .eq("proposal_id", proposal.id)
      .maybeSingle();

    const [company, invoice] = await Promise.all([
      loadCompanyProfile(),
      loadInvoiceForProposal(proposal.id),
    ]);
    return toPublicProposal(proposal, items, sig ?? null, { company, invoice });

  });

/**
 * Emails a one-time code to the address the client entered. Verifying it before
 * the signature is accepted ties the signature to a controlled mailbox, which
 * is the practical difference between a bare and a well-evidenced eIDAS simple
 * electronic signature.
 */
export const requestSignatureCode = createServerFn({ method: "POST" })
  .inputValidator((input: { token: string; email: string }) => {
    if (!input.token || !/^[a-f0-9]{16,96}$/.test(input.token)) throw new Error("Invalid link");
    const email = String(input.email ?? "").trim().toLowerCase().slice(0, 254);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) throw new Error("Please enter a valid email address.");
    return { token: input.token, email };
  })
  .handler(async ({ data }): Promise<{ ok: true } | { error: string }> => {
    const { loadProposalByToken, logProposalEvent } = await import("./proposals.server");
    const found = await loadProposalByToken(data.token);
    if (!found) return { error: "Proposal not found" };
    const { proposal } = found;
    if (proposal.status === "signed" || proposal.status === "paid")
      return { error: "This proposal is already signed." };
    if (proposal.status === "draft" || proposal.status === "void")
      return { error: "This proposal is not available." };
    if (isExpired(proposal.valid_until)) return { error: "This proposal has expired." };

    const { ip } = requestMeta();
    const { createSignatureChallenge } = await import("./proposal-signing.server");
    const { code } = await createSignatureChallenge({
      proposalId: proposal.id,
      email: data.email,
      ip,
    });

    try {
      const [{ enqueueBrandedEmail }, { SignatureCodeEmail }] = await Promise.all([
        import("./email/transactional.server"),
        import("./email-templates/signature-code"),
      ]);
      const sent = await enqueueBrandedEmail({
        to: data.email,
        subject: `${code} — your signing code for ${proposal.number}`,
        label: "signature-code",
        locale: (proposal.language as "en" | "de") ?? "en",
        element: SignatureCodeEmail({
          code,
          proposalNumber: proposal.number,
          title: proposal.title,
        }),
      });
      if ("error" in sent) {
        console.error("signature code email failed", sent.error);
        return {
          error:
            "We couldn't send the code to this address. Please reply to your proposal email and we'll sign it with you directly.",
        };
      }
      if (sent.suppressed) {
        return {
          error:
            "This address has unsubscribed from our emails, so we can't send the code there. Please reply to your proposal email and we'll help you sign.",
        };
      }
    } catch (e) {
      console.error("signature code email failed", e);
      return { error: "Could not send the code. Please try again." };
    }

    await logProposalEvent(proposal.id, "signature_code_sent", {
      ip,
      meta: { email: data.email },
    });
    return { ok: true };
  });

export const signProposal = createServerFn({ method: "POST" })
  .inputValidator(
    (input: {
      token: string;
      signerName: string;
      addonIds: string[];
      agreed: boolean;
      billing: Partial<BillingDetails>;
      code: string;
    }) => {
      if (!input.token || !/^[a-f0-9]{16,96}$/.test(input.token)) throw new Error("Invalid link");
      const signerName = (input.signerName ?? "").trim();
      if (signerName.length < 3) throw new Error("Please type your full legal name.");
      if (signerName.length > 120) throw new Error("Name is too long.");
      if (!input.agreed) throw new Error("You must agree to the contract to sign.");
      const raw = input.billing ?? {};
      const str = (v: unknown, max = 160) => String(v ?? "").trim().slice(0, max);
      const billing: BillingDetails = {
        billing_name: str(raw.billing_name),
        billing_email: str(raw.billing_email, 254).toLowerCase(),
        billing_street: str(raw.billing_street),
        billing_postcode: str(raw.billing_postcode, 20),
        billing_city: str(raw.billing_city),
        billing_country: str(raw.billing_country, 2).toUpperCase(),
        billing_vat_id: str(raw.billing_vat_id, 20).toUpperCase() || null,
        billing_reference: str(raw.billing_reference, 80) || null,
      };
      const problems = billingProblems(billing);
      if (problems.includes("billing_email"))
        throw new Error("Please enter a valid email address for your receipt and invoice.");
      if (problems.length) throw new Error("Please complete your billing address.");

      const code = String(input.code ?? "").replace(/\D/g, "").slice(0, 6);
      if (code.length !== 6)
        throw new Error("Enter the 6-digit code we emailed you to confirm your address.");

      return {
        token: input.token,
        signerName,
        agreed: true,
        addonIds: Array.isArray(input.addonIds) ? input.addonIds : [],
        billing,
        code,
      };
    },
  )
  .handler(async ({ data }): Promise<{ ok: true; hash: string; total: number } | { error: string }> => {
    const { loadProposalByToken, canonicalContent, sha256Hex, logProposalEvent } = await import(
      "./proposals.server"
    );
    const { computeTaxTotals, determineVatMode, isValidVatId } = await import("./tax");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const db = supabaseAdmin as any;

    const found = await loadProposalByToken(data.token);
    if (!found) return { error: "Proposal not found" };
    const { proposal, items } = found;
    if (proposal.status === "signed" || proposal.status === "paid")
      return { error: "This proposal is already signed." };
    if (proposal.status === "draft" || proposal.status === "void")
      return { error: "This proposal is not available." };
    if (isExpired(proposal.valid_until)) return { error: "This proposal has expired." };

    const billing = data.billing;
    if (billing.billing_vat_id && !isValidVatId(billing.billing_vat_id))
      return { error: "That VAT ID does not look valid. Leave it empty if you do not have one." };

    const validAddonIds = items.filter((i) => i.is_addon).map((i) => i.id);
    const addonIds = data.addonIds.filter((id) => validAddonIds.includes(id));
    const total = computeTotalCents(items, addonIds);
    const vatMode = determineVatMode({
      country: billing.billing_country,
      vatId: billing.billing_vat_id,
    });
    const totals = computeTaxTotals(taxLinesFor(items, addonIds), vatMode);
    const hash = await sha256Hex(canonicalContent(proposal, items, addonIds, billing));
    const { ip, ua } = requestMeta();
    const signedAt = new Date().toISOString();

    // The emailed code must check out before anything is recorded.
    const signerEmail = billing.billing_email || proposal.client_email || "";
    const {
      verifySignatureChallenge,
      buildDocumentSnapshot,
      requestTimestampToken,
    } = await import("./proposal-signing.server");
    const verification = await verifySignatureChallenge({
      proposalId: proposal.id,
      email: signerEmail,
      code: data.code,
    });
    if ("error" in verification) return { error: verification.error };

    const { loadCompanyProfile } = await import("./proposals.server");
    const company = await loadCompanyProfile().catch(() => null);
    const snapshot = buildDocumentSnapshot({
      proposal,
      items,
      addonIds,
      billing,
      company,
      totals,
      vatMode,
    });
    const stamp = await requestTimestampToken(hash);
    const acceptLanguage = getRequest()?.headers?.get("accept-language") ?? null;

    const { error } = await db.from("proposal_signatures").insert({
      proposal_id: proposal.id,
      signer_name: data.signerName,
      signer_email: signerEmail,
      ip_address: ip,
      user_agent: ua,
      content_hash: hash,
      selected_addon_ids: addonIds,
      total_cents: total,
      signed_at: signedAt,
      email_verified_at: verification.verifiedAt,
      document_snapshot: snapshot,
      timestamp_token: stamp?.token ?? null,
      timestamp_authority: stamp?.authority ?? null,
      accept_language: acceptLanguage,
    });
    if (error) return { error: "Could not record the signature. Please try again." };

    await db
      .from("proposals")
      .update({
        status: "signed",
        signed_at: signedAt,
        amount_total_cents: total,
        vat_mode: vatMode,
        ...billing,
      })
      .eq("id", proposal.id);

    await logProposalEvent(proposal.id, "signed", {
      ip,
      ua,
      meta: {
        signer_name: data.signerName,
        total_cents: total,
        content_hash: hash,
        vat_mode: vatMode,
        vat_cents: totals.vatCents,
        gross_cents: totals.grossCents,
      },
    });

    // Signing turns the lead into a real client: create/link a profile so they
    // show up in the CRM immediately, before any payment happens.
    try {
      const contactEmail = billing.billing_email || proposal.client_email;
      if (contactEmail) {
        const { ensureCheckoutProfile } = await import("./checkout-account.server");
        const { userId } = await ensureCheckoutProfile({
          email: contactEmail,
          name: billing.billing_name || data.signerName || proposal.client_name,
          statusIfNew: "reviewing",
        });
        await db
          .from("proposals")
          .update({
            profile_id: userId,
            client_email: proposal.client_email ?? contactEmail,
            client_name: proposal.client_name ?? (billing.billing_name || data.signerName),
          })
          .eq("id", proposal.id);
        await logProposalEvent(proposal.id, "client_linked", {
          meta: { profile_id: userId, email: contactEmail },
        });
      }
    } catch (e) {
      console.error("signProposal: could not link client profile", e);
    }


    try {
      const [{ enqueueBrandedEmail }, { ProposalSignedEmail }, { formatMoney, cycleLabel }] =
        await Promise.all([
          import("./email/transactional.server"),
          import("./email-templates/proposal-signed"),
          import("./proposals-types"),
        ]);
      const { origin } = requestMeta();
      const props = {
        proposalNumber: proposal.number,
        title: proposal.title,
        signerName: data.signerName,
        signedAt: new Date(signedAt).toUTCString(),
        priceLabel: `${formatMoney(total, proposal.currency)} ${cycleLabel(proposal.billing_cycle)}`,
        contentHash: hash,
        proposalUrl: `${origin}/p/${proposal.token}`,
      };
      const recipients = Array.from(
        new Set(
          [billing.billing_email, proposal.client_email, "jon@vibing.inc"].filter(
            (v): v is string => !!v,
          ),
        ),
      );

      for (const to of recipients) {
        await enqueueBrandedEmail({
          to,
          subject: `Signed — ${proposal.number} ${proposal.title}`,
          label: "proposal-signed",
          locale: (proposal.language as "en" | "de") ?? "en",
          element: ProposalSignedEmail(props),
        });
      }
    } catch (e) {
      console.error("signed email failed", e);
    }

    return { ok: true, hash, total };
  });

export const declineProposal = createServerFn({ method: "POST" })
  .inputValidator((input: { token: string; reason?: string }) => {
    if (!input.token || !/^[a-f0-9]{16,96}$/.test(input.token)) throw new Error("Invalid link");
    return { token: input.token, reason: (input.reason ?? "").slice(0, 1000) };
  })
  .handler(async ({ data }): Promise<{ ok: true } | { error: string }> => {
    const { loadProposalByToken, logProposalEvent } = await import("./proposals.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const db = supabaseAdmin as any;
    const found = await loadProposalByToken(data.token);
    if (!found) return { error: "Proposal not found" };
    if (found.proposal.status === "signed" || found.proposal.status === "paid")
      return { error: "This proposal is already signed." };
    await db
      .from("proposals")
      .update({ status: "declined", declined_at: new Date().toISOString() })
      .eq("id", found.proposal.id);
    const { ip, ua } = requestMeta();
    await logProposalEvent(found.proposal.id, "declined", { ip, ua, meta: { reason: data.reason } });
    return { ok: true };
  });

export const startProposalPayment = createServerFn({ method: "POST" })
  .inputValidator((input: { token: string }) => {
    if (!input.token || !/^[a-f0-9]{16,96}$/.test(input.token)) throw new Error("Invalid link");
    return input;
  })
  .handler(async ({ data }): Promise<{ clientSecret: string } | { error: string }> => {
    const { loadProposalByToken, logProposalEvent } = await import("./proposals.server");
    const { computeTaxTotals } = await import("./tax");
    const { proposalVatMode } = await import("./proposals-types");
    const { createProposalCheckoutSession } = await import("./stripe.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const db = supabaseAdmin as any;

    const found = await loadProposalByToken(data.token);
    if (!found) return { error: "Proposal not found" };
    const { proposal, items } = found;
    if (proposal.status === "paid") return { error: "This proposal is already paid." };
    if (proposal.status !== "signed") return { error: "Sign the proposal before paying." };

    const { data: sig } = await db
      .from("proposal_signatures")
      .select("total_cents, selected_addon_ids")
      .eq("proposal_id", proposal.id)
      .maybeSingle();
    const addonIds =
      (sig?.selected_addon_ids as string[] | undefined) ??
      items.filter((i) => i.is_addon && i.default_selected).map((i) => i.id);
    const mode = proposalVatMode(proposal.vat_mode);
    const totals = computeTaxTotals(taxLinesFor(items, addonIds), mode);
    // Stripe always charges the gross amount; VAT is broken out on the invoice.
    const amount =
      totals.grossCents > 0
        ? totals.grossCents
        : ((sig?.total_cents as number | undefined) ?? proposal.amount_total_cents ?? 0);
    if (amount <= 0) return { error: "This proposal has no payable amount." };

    const { origin } = requestMeta();
    const session = await createProposalCheckoutSession({
      proposalId: proposal.id,
      title: `${proposal.number} — ${proposal.title}`,
      amountCents: amount,
      currency: proposal.currency,
      billingCycle: proposal.billing_cycle,
      customerEmail: proposal.billing_email ?? proposal.client_email,
      returnUrl: `${origin}/p/${proposal.token}?paid=1&session_id={CHECKOUT_SESSION_ID}`,
    });
    if ("error" in session) return { error: session.error };

    await db.from("proposals").update({ stripe_session_id: session.sessionId }).eq("id", proposal.id);
    await logProposalEvent(proposal.id, "payment_started", {
      meta: { amount_cents: amount, net_cents: totals.netCents, vat_cents: totals.vatCents },
    });
    return { clientSecret: session.clientSecret };
  });

export const confirmProposalPayment = createServerFn({ method: "POST" })
  .inputValidator((input: { token: string; sessionId: string }) => {
    if (!input.token || !/^[a-f0-9]{16,96}$/.test(input.token)) throw new Error("Invalid link");
    if (!input.sessionId || !/^cs_[a-zA-Z0-9_]+$/.test(input.sessionId))
      throw new Error("Invalid session");
    return input;
  })
  .handler(async ({ data }): Promise<{ paid: boolean; invoiceNumber?: string }> => {
    const { loadProposalByToken, logProposalEvent, issueInvoiceForProposal } = await import(
      "./proposals.server"
    );
    const { retrieveCheckoutSession } = await import("./stripe.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const db = supabaseAdmin as any;

    const found = await loadProposalByToken(data.token);
    if (!found) return { paid: false };
    if (found.proposal.status === "paid") {
      const issued = await issueInvoiceForProposal(found.proposal.id);
      return { paid: true, ...("number" in issued ? { invoiceNumber: issued.number } : {}) };
    }

    const session = await retrieveCheckoutSession(data.sessionId);
    const paid =
      !!session &&
      (session as { payment_status?: string; status?: string }).payment_status === "paid";
    if (!paid) return { paid: false };

    const paidAt = new Date().toISOString();
    await db.from("proposals").update({ status: "paid", paid_at: paidAt }).eq("id", found.proposal.id);
    await logProposalEvent(found.proposal.id, "paid", { meta: { session_id: data.sessionId } });

    const issued = await issueInvoiceForProposal(found.proposal.id, {
      paidAt,
      paymentMethod: "card",
      paymentRef: data.sessionId,
    });
    if ("error" in issued) console.error("invoice issue failed", issued.error);

    const invoiceNumber = "number" in issued ? issued.number : null;
    try {
      const { computeTaxTotals } = await import("./tax");
      const { proposalVatMode } = await import("./proposals-types");
      const { ProposalPaidEmail } = await import("./email-templates/proposal-paid");
      const { enqueueBrandedEmail } = await import("./email/transactional.server");
      const { formatMoney } = await import("./proposals-types");


      const proposal = found.proposal;
      const { data: sig } = await db
        .from("proposal_signatures")
        .select("signer_name, selected_addon_ids")
        .eq("proposal_id", proposal.id)
        .maybeSingle();
      const addonIds =
        (sig?.selected_addon_ids as string[] | undefined) ??
        found.items.filter((i) => i.is_addon && i.default_selected).map((i) => i.id);
      const totals = computeTaxTotals(
        taxLinesFor(found.items, addonIds),
        proposalVatMode(proposal.vat_mode),
      );
      const { origin } = requestMeta();
      const props = {
        proposalNumber: proposal.number,
        title: proposal.title,
        signerName: (sig?.signer_name as string | undefined) ?? proposal.client_name ?? "there",
        paidAt: new Date(paidAt).toUTCString(),
        netLabel: formatMoney(totals.netCents, proposal.currency),
        vatLabel: totals.vatCents > 0 ? formatMoney(totals.vatCents, proposal.currency) : null,
        grossLabel: formatMoney(totals.grossCents, proposal.currency),
        invoiceNumber,
        proposalUrl: `${origin}/p/${proposal.token}`,
        documentUrl: `${origin}/p/${proposal.token}/contract`,
      };
      const recipients = Array.from(
        new Set(
          [proposal.billing_email, proposal.client_email, "jon@vibing.inc"].filter(
            (v): v is string => !!v,
          ),
        ),
      );
      for (const to of recipients) {
        await enqueueBrandedEmail({
          to,
          subject: `Payment received — ${proposal.number} ${proposal.title}`,
          label: "proposal-paid",
          locale: (proposal.language as "en" | "de") ?? "en",
          element: ProposalPaidEmail(props),
        });
      }
    } catch (e) {
      console.error("paid email failed", e);
    }

    return { paid: true, ...(invoiceNumber ? { invoiceNumber } : {}) };

  });

/* --------- share metadata (head tags): heading + recipient only --------- */

export type ProposalShareMeta = {
  title: string;
  description: string;
  imageUrl: string;
  language: "en" | "de";
};

export const getProposalShareMeta = createServerFn({ method: "GET" })
  .inputValidator((input: { token: string }) => {
    if (!input.token || !/^[a-f0-9]{16,96}$/.test(input.token)) throw new Error("Invalid link");
    return input;
  })
  .handler(async ({ data }): Promise<ProposalShareMeta | null> => {
    const { loadProposalMeta } = await import("./proposal-og.server");
    const { proposalShareTitle, proposalShareDescription } = await import("./proposal-share");
    const meta = await loadProposalMeta(data.token);
    if (!meta || meta.status === "void") return null;
    const { origin } = requestMeta();
    const version = meta.updated_at ? Date.parse(meta.updated_at) || 0 : 0;
    return {
      title: proposalShareTitle(meta),
      description: proposalShareDescription(meta),
      imageUrl: `${origin}/api/public/og/proposal/${data.token}?v=${version}`,
      language: meta.language,
    };
  });

/**
 * Stores the link-preview PNG rendered in the browser. The token is the access
 * secret for the proposal, and the image carries no pricing information.
 */
export const storeProposalOgImage = createServerFn({ method: "POST" })
  .inputValidator((input: { token: string; dataUrl: string }) => {
    if (!input.token || !/^[a-f0-9]{16,96}$/.test(input.token)) throw new Error("Invalid link");
    if (!input.dataUrl?.startsWith("data:image/png;base64,")) throw new Error("Invalid image");
    if (input.dataUrl.length > 4_000_000) throw new Error("Image too large");
    return input;
  })
  .handler(async ({ data }): Promise<{ stored: boolean }> => {
    const { loadProposalMeta, ogObjectPath, writeStoredOg } = await import("./proposal-og.server");
    const meta = await loadProposalMeta(data.token);
    if (!meta || meta.status === "void") return { stored: false };
    const path = ogObjectPath(data.token);
    if (!path) return { stored: false };

    const binary = atob(data.dataUrl.slice("data:image/png;base64,".length));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    // PNG magic number — reject anything that is not an actual PNG.
    if (bytes[0] !== 0x89 || bytes[1] !== 0x50 || bytes[2] !== 0x4e || bytes[3] !== 0x47) {
      throw new Error("Invalid image");
    }
    await writeStoredOg(path, bytes);
    return { stored: true };
  });


export type SignatureCertificate = {
  proposalNumber: string;
  title: string;
  signerName: string;
  signerEmail: string | null;
  signedAt: string;
  emailVerifiedAt: string | null;
  contentHash: string;
  ipAddress: string | null;
  userAgent: string | null;
  acceptLanguage: string | null;
  timestampAuthority: string | null;
  hasTimestampToken: boolean;
  totalCents: number;
  currency: string;
};

/**
 * Public audit certificate for a signed proposal — the human-readable evidence
 * record behind the signature.
 */
export const getSignatureCertificate = createServerFn({ method: "GET" })
  .inputValidator((input: { token: string }) => {
    if (!input.token || !/^[a-f0-9]{16,96}$/.test(input.token)) throw new Error("Invalid link");
    return { token: input.token };
  })
  .handler(async ({ data }): Promise<SignatureCertificate | null> => {
    const { loadProposalByToken } = await import("./proposals.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const db = supabaseAdmin as any;
    const found = await loadProposalByToken(data.token);
    if (!found) return null;
    const { proposal } = found;
    const { data: rows } = await db
      .from("proposal_signatures")
      .select(
        "signer_name, signer_email, signed_at, email_verified_at, content_hash, ip_address, user_agent, accept_language, timestamp_authority, timestamp_token, total_cents",
      )
      .eq("proposal_id", proposal.id)
      .order("signed_at", { ascending: false })
      .limit(1);
    const sig = rows?.[0];
    if (!sig) return null;
    return {
      proposalNumber: proposal.number,
      title: proposal.title,
      signerName: sig.signer_name,
      signerEmail: sig.signer_email ?? null,
      signedAt: sig.signed_at,
      emailVerifiedAt: sig.email_verified_at ?? null,
      contentHash: sig.content_hash,
      ipAddress: sig.ip_address ?? null,
      userAgent: sig.user_agent ?? null,
      acceptLanguage: sig.accept_language ?? null,
      timestampAuthority: sig.timestamp_authority ?? null,
      hasTimestampToken: !!sig.timestamp_token,
      totalCents: sig.total_cents,
      currency: proposal.currency,
    };
  });
